'use client';

import Link from 'next/link';
import './v4-shell.css';
import { GiftCardsNav } from './GiftCardsNav';
import { GIFT_CARDS_ENABLED } from '@/lib/flags';
import { usePathname, useRouter } from 'next/navigation';
import { ShoppingCart, Heart, Search, Gamepad2, Sun, Moon, X, Menu, ArrowRight, Tag, Layers, Zap, UserCircle } from 'lucide-react';
import { useState, useEffect, useLayoutEffect, useRef, useCallback, memo } from 'react';
import { t } from '@/lib/i18n';
import { usePriceConvert } from '@/lib/PriceContext';
import type { CatalogIndexItem } from '@/types/catalog';

function highlight(text: string, query: string): React.ReactNode {
  if (!query || query.length < 2) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: 'color-mix(in srgb, var(--gs-green) 18%, transparent)', color: 'var(--gs-green)', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function fmtPrice(p: number | null, c: string | null): string {
  if (!p || !c) return '';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: c, maximumFractionDigits: 2 }).format(p);
  } catch { return `${p}`; }
}

const PLAT_LABEL: Record<string, string> = {
  XBOXSERIESX: 'XSX', XBOXONE: 'ONE', PC: 'PC',
};
const PLAT_CLS: Record<string, string> = {
  XBOXSERIESX: 'p-xsx', XBOXONE: 'p-xone', PC: 'p-pc',
};

// -- Search Dropdown — defined OUTSIDE Header to prevent remounting ----------
interface DropdownProps {
  results: CatalogIndexItem[];
  searchVal: string;
  catalogLoading: boolean;
  activeIdx: number;
  isMobile?: boolean;
  itemRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>;
  onGoToProduct: (id: string) => void;
  onGoToAll: () => void;
  onSetActiveIdx: (i: number) => void;
  totalHits: number;
  lang: string;
}

const SearchDropdown = memo(function SearchDropdown({
  results, searchVal, catalogLoading, activeIdx, isMobile = false,
  itemRefs, onGoToProduct, onGoToAll, onSetActiveIdx, totalHits, lang,
}: DropdownProps) {
  const { convert: fmtConverted } = usePriceConvert();
  return (
    <div className={`v4h-drop${isMobile ? ' v4h-drop-mobile' : ''}`} role="listbox">
      {catalogLoading && results.length === 0 && (
        <div style={{ padding: '8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div className="v4h-drop-skel" />
          <div className="v4h-drop-skel" style={{ width: '75%' }} />
          <div className="v4h-drop-skel" style={{ width: '55%' }} />
        </div>
      )}
      {!catalogLoading && results.length === 0 && searchVal.length >= 2 && (
        <div className="v4h-drop-empty">
          <Search size={20} style={{ opacity: .25 }} />
          <span>{t('common_not_found', lang)} «{searchVal}»</span>
        </div>
      )}
      {results.map((item, i) => {
        const isActive = activeIdx === i;
        const price = fmtConverted(item.min_key_usd ?? item.price) ?? fmtPrice(item.min_key_usd ?? item.price, item.currency);
        const plats = (item.platforms ?? []).filter(p => PLAT_LABEL[p]).slice(0, 3);
        return (
          <button
            key={item.product_id}
            ref={el => { itemRefs.current[i] = el; }}
            className={`v4h-drop-item${isActive ? ' active' : ''}`}
            onClick={() => onGoToProduct(item.product_id)}
            onMouseEnter={() => onSetActiveIdx(i)}
            role="option"
            aria-selected={isActive}
          >
            <div className="v4h-drop-cover">
              {item.poster_w300
                ? <img src={item.poster_w300} alt="" loading="lazy" />
                : <Gamepad2 size={14} style={{ opacity: .3 }} />
              }
            </div>
            <div className="v4h-drop-info">
              <span className="v4h-drop-title">{highlight(item.title, searchVal)}</span>
              <div className="v4h-drop-meta">
                {item.developer_name && <span>{item.developer_name}</span>}
                {plats.length > 0 && (
                  <div className="v4h-drop-plats">
                    {plats.map(p => <span key={p} className={`v4h-drop-plat ${PLAT_CLS[p] ?? ''}`}>{PLAT_LABEL[p]}</span>)}
                  </div>
                )}
              </div>
            </div>
            <div className="v4h-drop-side">
              {price && <span className="v4h-drop-price">{price}</span>}
              {item.is_on_sale && <span className="v4h-drop-sale">Sale</span>}
              <span className={`v4h-drop-badge ${item.type === 'game' ? 'is-game' : 'is-dlc'}`}>
                {item.type === 'game' ? t('card_game', lang) : t('card_dlc', lang)}
              </span>
            </div>
          </button>
        );
      })}
      {results.length > 0 && (
        <button
          className={`v4h-drop-all${activeIdx === results.length ? ' active' : ''}`}
          onClick={onGoToAll}
          onMouseEnter={() => onSetActiveIdx(results.length)}
        >
          <Search size={13} />
          <span>{t('common_see_all', lang)} <strong>«{searchVal}»</strong></span>
          {totalHits > 7 && <span className="v4h-drop-count">{totalHits}</span>}
          <ArrowRight size={13} style={{ marginLeft: 'auto', opacity: .5 }} />
        </button>
      )}
    </div>
  );
});

// -- Header ------------------------------------------------------------------
export function Header() {
  const pathname = usePathname();
  const router   = useRouter();
  const { lang } = usePriceConvert();

  const [cartCount, setCartCount] = useState(0);
  const [wishCount, setWishCount] = useState(0);
  const [theme, setTheme]         = useState<'dark' | 'light'>('dark');
  const [searchVal, setSearchVal] = useState('');
  const [results, setResults]     = useState<CatalogIndexItem[]>([]);
  const [showDrop, setShowDrop]   = useState(false);
  const [totalHits, setTotalHits] = useState(0);
  const [activeIdx, setActiveIdx] = useState(-1);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [authed, setAuthed] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef       = useRef<HTMLInputElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const wrapRef        = useRef<HTMLDivElement>(null);
  const itemRefs       = useRef<(HTMLButtonElement | null)[]>([]);

  // -- cart / wishlist counts ---------------------------------------------
  // -- initial counts from inline script or localStorage ----------------
  useLayoutEffect(() => {
    const win = window as Window & { __GScc?: number; __GSwc?: number };
    try {
      const cart = JSON.parse(localStorage.getItem('cart') ?? '[]');
      const wish = JSON.parse(localStorage.getItem('wishlist') ?? '[]');
      const cc = win.__GScc ?? cart.reduce((s: number, i: { quantity?: number }) => s + (i.quantity ?? 1), 0);
      const wc = win.__GSwc ?? wish.length;
      setCartCount(cc);
      setWishCount(wc);
    } catch {}
    setAuthed(document.cookie.includes('gs_authed=1'));
  }, []);

  useEffect(() => {
    const update = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('cart') ?? '[]');
        const wish = JSON.parse(localStorage.getItem('wishlist') ?? '[]');
        setCartCount(cart.reduce((s: number, i: { quantity: number }) => s + (i.quantity ?? 1), 0));
        setWishCount(wish.length);
      } catch {}
    };
    window.addEventListener('cart-updated', update);
    window.addEventListener('wishlist-updated', update);
    return () => {
      window.removeEventListener('cart-updated', update);
      window.removeEventListener('wishlist-updated', update);
    };
  }, []);


  // -- auth state — reads indicator cookie, zero network requests ------
  useEffect(() => {
    const check = () => setAuthed(document.cookie.includes('gs_authed=1'));
    window.addEventListener('auth-updated', check);
    return () => window.removeEventListener('auth-updated', check);
  }, []);
  // -- theme --------------------------------------------------------------
  useLayoutEffect(() => {
    const saved = localStorage.getItem('theme') as 'dark' | 'light' | null;
    if (saved) { setTheme(saved); document.documentElement.setAttribute('data-theme', saved); }
  }, []);

  // -- close on navigate --------------------------------------------------
  useEffect(() => {
    if (!pathname.startsWith('/catalog')) setSearchVal('');
    setShowDrop(false); setMenuOpen(false); setMobileSearch(false); setActiveIdx(-1);
  }, [pathname, router]);

  // -- close on outside click ---------------------------------------------
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowDrop(false); setActiveIdx(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // -- server-side search ---------------------------------------------------
  // Раніше: повний каталог (34k товарів, ~3.5MB gzip) вантажився в браузер на
  // перший фокус пошуку і Fuse.js будував fuzzy-index над усім масивом
  // client-side (блокуюча CPU-робота на головному потоці). Тепер — легкий
  // запит до /api/search (pg_trgm-індекс на сервері, ~30-40мс), debounce +
  // AbortController відміняє застарілі відповіді (race protection).
  const abortRef = useRef<AbortController | null>(null);

  const runSearch = useCallback(async (value: string) => {
    if (value.trim().length < 2) { setResults([]); setShowDrop(false); return; }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setCatalogLoading(true);
    try {
      const res = await fetch(`/api/search?q=${encodeURIComponent(value)}`, { signal: ctrl.signal });
      const data: { items: CatalogIndexItem[] } = await res.json();
      setTotalHits(data.items.length);
      setResults(data.items);
      setActiveIdx(-1);
      setShowDrop(true);
    } catch (e) {
      if ((e as Error).name !== 'AbortError') { /* silently fail */ }
    } finally {
      if (abortRef.current === ctrl) setCatalogLoading(false);
    }
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchVal(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length < 2) { setResults([]); setShowDrop(false); return; }
    setShowDrop(true);
    debounceRef.current = setTimeout(() => { runSearch(value); }, 300);
  }, [runSearch]);

  const goToProduct = useCallback((productId: string) => {
    setShowDrop(false); setMobileSearch(false); setSearchVal(''); setResults([]); setActiveIdx(-1);
    router.push(`/product/${productId}`);
  }, [router]);

  const goToAllResults = useCallback(() => {
    const q = searchVal;
    setShowDrop(false); setMobileSearch(false); setActiveIdx(-1);
    router.push(`/catalog?q=${encodeURIComponent(q)}`);
    // Якщо клієнт уже на /catalog, router.push не ремонтує shell і q не застосується —
    // тригеримо подію пошуку каталогу (обробник у CatalogShell читає e.detail).
    setTimeout(() => window.dispatchEvent(new CustomEvent('catalog:search', { detail: q })), 0);
  }, [router, searchVal]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDrop) return;
    const total = results.length + 1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(prev => Math.min(prev + 1, total - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && activeIdx < results.length) goToProduct(results[activeIdx].product_id);
      else goToAllResults();
    } else if (e.key === 'Escape') {
      setShowDrop(false); setActiveIdx(-1);
    }
  }, [showDrop, activeIdx, results, goToProduct, goToAllResults]);

  const clearSearch = useCallback(() => {
    setSearchVal(''); setResults([]); setShowDrop(false); setActiveIdx(-1);
    inputRef.current?.focus();
    mobileInputRef.current?.focus();
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('theme', next);
      document.documentElement.setAttribute('data-theme', next);
      return next;
    });
  }, []);

  const langTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toggleLang = useCallback(() => {
    // Поточна мова з localStorage (джерело істини) — надійно при швидких кліках,
    // коли реактивний стан ще не встиг оновитись.
    const cur = (typeof localStorage !== 'undefined' && localStorage.getItem('lang')) || lang;
    const next = cur === 'en' ? 'ru' : cur === 'ru' ? 'uk' : 'en';
    localStorage.setItem('lang', next);
    document.cookie = `lang=${next}; path=/; max-age=${365 * 24 * 3600}; SameSite=Lax`;
    // Миттєве оновлення клієнтського UI (усі useT-споживачі через PriceContext).
    window.dispatchEvent(new Event('lang-change'));
    // Серверний refresh + збереження в профіль — дебаунс, щоб швидке перемикання
    // не спамило RSC-запитами й API (саме це «підлагувало» інтерфейс).
    if (langTimer.current) clearTimeout(langTimer.current);
    langTimer.current = setTimeout(() => {
      if (document.cookie.includes('gs_authed=1')) {
        fetch('/api/account/profile', {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locale: next }),
        }).catch(() => {});
      }
      router.refresh();
    }, 500);
  }, [lang, router]);

  const openMobileSearch = useCallback(() => {
    setMobileSearch(true); setMenuOpen(false);
    setTimeout(() => mobileInputRef.current?.focus(), 80);
  }, []);

  const NAV_LINKS = [
    { href: '/catalog',  label: t('nav_catalog',  lang) },
    { href: '/sale',     label: t('nav_sales',    lang) },
    { href: '/releases', label: t('nav_new',      lang) },
    { href: '/preorder', label: t('nav_preorder', lang) },
    ...(GIFT_CARDS_ENABLED ? [{ href: '/gift-cards', label: t('nav_more', lang) }] : []),
  ];

  return (
    <>
      <header className="v4h">
        {/* Logo */}
        <Link href="/" className="v4h-logo">
          <div className="v4h-logo-mark">
            <Gamepad2 size={20} color="#fff" strokeWidth={1.8} />
          </div>
          <div className="v4h-logo-text">
            <div className="v4h-logo-name">Games<em>Sales</em></div>
            <div className="v4h-logo-sub">Xbox Store</div>
          </div>
        </Link>

        {/* Search wrap */}
        <div className="v4h-search-wrap" ref={wrapRef}>
          <div className={`v4h-search${showDrop ? ' v4h-search--open' : ''}`}>
            <Search size={15} style={{ flexShrink: 0, color: showDrop ? 'var(--gs-green)' : 'var(--v4-fg-4)', transition: 'color .2s' }} />
            <input
              ref={inputRef}
              placeholder={t('search_placeholder', lang)}
              value={searchVal}
              onChange={e => handleSearchChange(e.target.value)}
              onFocus={() => { if (results.length > 0) setShowDrop(true); }}
              onKeyDown={handleKeyDown}
              spellCheck={false}
              autoComplete="off"
              aria-autocomplete="list"
              aria-expanded={showDrop}
            />
            {searchVal && (
              <button className="v4h-search-clear" onClick={clearSearch} tabIndex={-1} aria-label={t('aria_search_clear', lang)}>
                <X size={12} />
              </button>
            )}
          </div>
          {showDrop && (
            <SearchDropdown
              results={results}
              searchVal={searchVal}
              catalogLoading={catalogLoading}
              activeIdx={activeIdx}
              itemRefs={itemRefs}
              onGoToProduct={goToProduct}
              onGoToAll={goToAllResults}
              onSetActiveIdx={setActiveIdx}
              totalHits={totalHits}
              lang={lang}
            />
          )}
        </div>

        {/* Desktop nav */}
        <nav className="v4h-nav">
          {NAV_LINKS.map(({ href, label }) => (
            href === '/gift-cards'
              ? <GiftCardsNav key={href} href={href} label={label} />
              : <Link key={href} href={href} className="v4h-nav-link" prefetch={true}>{label}</Link>
          ))}
        </nav>

        <div className="v4h-spacer" />

        {/* Mobile search trigger */}
        <button className="v4h-icon-btn v4h-mobile-search-btn" onClick={openMobileSearch} aria-label={t('aria_search', lang)}>
          <Search size={16} />
        </button>

        {/* Theme toggle */}
        <button onClick={toggleTheme} className="v4h-icon-btn" aria-label={t('aria_theme', lang)}>
          {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
        </button>

        {/* Lang toggle */}
        <button onClick={toggleLang} className="v4h-icon-btn v4h-icon-btn--lang" aria-label={t('aria_lang', lang)}>
          {lang}
        </button>

        {/* Auth button */}
        <Link
          href={authed ? '/account' : '/login'}
          aria-label={authed ? t('aria_account', lang) : t('aria_login', lang)}
          className={`v4h-icon-btn${authed ? ' v4h-icon-btn--auth-on' : ''}`}
        >
          <UserCircle size={17} />
          {authed && <span className="v4h-dot" />}
        </Link>

        {/* Wishlist + Cart — hidden on mobile (in bottom nav) */}
        <div className="hdr-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <Link href="/wishlist" className={`v4h-icon-btn${wishCount > 0 ? ' v4h-icon-btn--wish-on' : ''}`} aria-label="Wishlist">
            <Heart size={15} />
            {wishCount > 0 && <span className="v4h-count">{wishCount}</span>}
          </Link>
          <Link href="/cart" className="v4h-cart">
            <ShoppingCart size={15} />
            <span className="v4h-cart-label">{t('footer_cart', lang)}</span>
            {cartCount > 0 && <span className="v4h-count">{cartCount}</span>}
          </Link>
        </div>

        {/* Hamburger — secondary nav (visible ≤1100px) */}
        <button className="v4h-icon-btn v4h-burger" onClick={() => setMenuOpen(v => !v)} aria-label={t('aria_menu', lang)}>
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </header>

      {/* Mobile search overlay */}
      {mobileSearch && (
        <div
          className="v4h-msearch-ov"
          onClick={e => { if (e.target === e.currentTarget) { setMobileSearch(false); clearSearch(); } }}
        >
          <div style={{ width: '100%' }}>
            <div className="v4h-msearch-bar">
              <Search size={16} style={{ flexShrink: 0, color: searchVal ? 'var(--gs-green)' : 'var(--v4-fg-4)', transition: 'color .2s' }} />
              <input
                ref={mobileInputRef}
                placeholder={t('search_placeholder', lang)}
                value={searchVal}
                onChange={e => handleSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                spellCheck={false}
                autoComplete="off"
              />
              {searchVal && <button className="v4h-search-clear" onClick={clearSearch}><X size={13} /></button>}
              <button className="v4h-msearch-close" onClick={() => { setMobileSearch(false); clearSearch(); }}>{t('common_cancel', lang)}</button>
            </div>
            {(showDrop || (searchVal.length >= 2)) && (
              <SearchDropdown
                results={results}
                searchVal={searchVal}
                catalogLoading={catalogLoading}
                activeIdx={activeIdx}
                isMobile
                itemRefs={itemRefs}
                onGoToProduct={goToProduct}
                onGoToAll={goToAllResults}
                onSetActiveIdx={setActiveIdx}
                totalHits={totalHits}
                lang={lang}
              />
            )}
          </div>
        </div>
      )}

      {/* Mobile menu drawer */}
      {menuOpen && (
        <div className="v4h-mmenu" onClick={e => { if (e.target === e.currentTarget) setMenuOpen(false); }}>
          <nav className="v4h-mnav">
            {NAV_LINKS.map(({ href, label }) => (
              <Link key={href} href={href} className="v4h-mnav-link" onClick={() => setMenuOpen(false)}>{label}</Link>
            ))}
            <div className="v4h-mnav-divider" />
            <div className="v4h-mnav-row">
              <button onClick={() => { toggleTheme(); setMenuOpen(false); }} className="v4h-mnav-btn">
                {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
                {theme === 'dark' ? t('theme_light', lang) : t('theme_dark', lang)}
              </button>
              <button onClick={() => { toggleLang(); setMenuOpen(false); }} className="v4h-mnav-btn">
                {lang === 'en' ? 'RU' : lang === 'ru' ? 'UK' : 'EN'}
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* -- Mobile bottom navigation bar -- */}
      <nav className="mbn" aria-label={t('nav_aria_label', lang)}>
        <Link href="/catalog" className={`mbn-tab${pathname.startsWith('/catalog') || pathname === '/' ? ' mbn-active' : ''}`}>
          <Layers size={20} strokeWidth={1.8} />
          {t('nav_catalog', lang)}
        </Link>
        <Link href="/sale" className={`mbn-tab${pathname === '/sale' ? ' mbn-active' : ''}`}>
          <Tag size={20} strokeWidth={1.8} />
          {t('nav_sales', lang)}
        </Link>
        <button className="mbn-tab mbn-search-tab" onClick={openMobileSearch} aria-label={t('aria_search', lang)}>
          <div className="mbn-search-pill">
            <Search size={18} strokeWidth={2} />
          </div>
          {t('aria_search', lang)}
        </button>
        <Link href="/preorder" className={`mbn-tab${pathname === '/preorder' ? ' mbn-active' : ''}`}>
          <Gamepad2 size={20} strokeWidth={1.8} />
          {t('nav_preorder', lang)}
        </Link>
        <Link href="/releases" className={`mbn-tab${pathname === '/releases' ? ' mbn-active' : ''}`}>
          <Zap size={20} strokeWidth={1.8} />
          {t('nav_new', lang)}
        </Link>
      </nav>
    </>
  );
}
